<template>
  <div class="st-tab">
    <!-- Structure list -->
    <div ref="rowList" class="st-list" :class="{ 'drop-child': dropHint && dropHint.asChild }" :key="listKey">
      <div
        v-for="row in rows"
        :key="row.id"
        class="st-row"
        :class="{ lvl3: rowKind(row) === 'h3', vis: rowKind(row) === 'visual', 'vis-child': rowKind(row) === 'visual' && row.parent_id, 'is-locked': row.locked, 'toc-row': row.visual_type === 'toc' }"
        :data-id="row.id"
      >
        <span class="st-drag" title="Drag to reorder">
          <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>
        </span>

        <template v-if="rowKind(row) === 'visual'">
          <!-- Pinned TOC row (design .toc-row): blue chip, system-built. -->
          <template v-if="row.visual_type === 'toc'">
            <span class="st-toc-chip" title="Updates automatically when you reorder sections">
              <span class="vico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/></svg></span>
              Table of contents
            </span>
          </template>
          <span v-else class="st-vis-chip" :class="visClass(row.visual_type)">
            <span class="vico" v-html="visIcon(row.visual_type)"></span>
            {{ visualLabel(row.visual_type) }}
          </span>
        </template>
        <template v-else>
          <button
            v-if="rowKind(row) === 'h2' || rowKind(row) === 'h3'"
            class="st-tag"
            :class="rowKind(row)"
            title="Switch H2 / H3"
            @click="$emit('toggle-kind', row)"
          >{{ rowKind(row).toUpperCase() }}</button>
          <span v-else class="st-tag" :class="rowKind(row)">{{ tagLabel(rowKind(row)) }}</span>
          <span
            class="st-text"
            :contenteditable="!row.locked && rowKind(row) !== 'intro'"
            spellcheck="false"
            @blur="$emit('edit-text', { row, text: $event.target.textContent })"
          >{{ rowText(row) }}</span>
          <span v-if="rowWordLabel(row)" class="st-word">{{ rowWordLabel(row) }}</span>
        </template>

        <div class="st-actions">
          <button v-if="row.visual_type !== 'toc'" class="st-act" :class="{ locked: row.locked }" :title="row.locked ? 'Unlock' : 'Lock - won\'t be changed by regenerate, SEO fixes or polish'" @click="$emit('lock', row)">
            <svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
          </button>
          <button v-if="row.visual_type !== 'toc'" class="st-act" title="Copy" @click="$emit('copy-row', row)">
            <svg viewBox="0 0 24 24"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
          </button>
          <button class="st-act" title="Remove" @click="$emit('delete-row', row)">
            <svg viewBox="0 0 24 24"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13h10l1-13"/></svg>
          </button>
        </div>
      </div>
    </div>
    <div v-if="!rows.length" class="st-empty">No outline yet.</div>

    <!-- Structure toolbar -->
    <div class="st-toolbar">
      <button class="st-tool" data-tip="Add new sub-heading" @click="$emit('add-heading')">
        <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
      </button>
      <button class="st-tool" data-tip="Add sub-headings in bulk" @click="$emit('add-bulk')">
        <svg viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h8"/><path d="M17 16v6M14 19h6"/></svg>
      </button>
      <button class="st-tool" data-tip="Regenerate all sub-headings" :disabled="busy" @click="$emit('regenerate-all')">
        <svg class="ic-solid" viewBox="0 0 24 24"><path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
      </button>
      <button class="st-tool" data-tip="Show history" @click="$emit('history')">
        <svg class="ic-solid" viewBox="0 0 24 24"><path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>
      </button>
      <button class="st-tool" data-tip="Download outline" @click="$emit('download')">
        <svg class="ic-solid" viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
      </button>
      <span class="st-tool-sep"></span>
      <div class="st-tool-wrap">
        <button class="st-tool wide" data-tip="Add a visual placeholder" @click="visMenu = !visMenu">
          <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 14l3-3 3 3 2-2 2 2"/></svg>
          Add visual
        </button>
        <div v-if="visMenu" class="st-vis-menu show">
          <button v-for="v in VISUAL_PLACEHOLDER_TYPES" :key="v.value" @click="addVisual(v.value)">
            <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 14l3-3 3 3 2-2 2 2"/></svg>
            {{ v.label }}
          </button>
        </div>
      </div>
      <button class="st-tool wide" data-tip="Copy the whole outline" @click="$emit('copy-outline')">
        <svg viewBox="0 0 24 24"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
        Copy outline
      </button>
    </div>
  </div>
</template>

<script>
import { Sortable } from "sortablejs";
import { VISUAL_PLACEHOLDER_TYPES } from "../../constants";

// Per-visual-type icon + color class (SVG path data from the design handoff).
const VIS = {
  image: { cls: "image", svg: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="M21 16l-5-5L5 20"/>' },
  table: { cls: "table", svg: '<rect x="3" y="4" width="18" height="16" rx="1"/><path d="M3 10h18M9 4v16"/>' },
  list: { cls: "list", svg: '<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>' },
  form: { cls: "form", svg: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 8h10M7 12h10M7 16h6"/>' },
  key_highlights: { cls: "highlights", svg: '<path d="M13 2L3 14h7l-1 8 10-12h-7z"/>' },
  brief_overview: { cls: "sources", svg: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>' },
  faq: { cls: "faqv", svg: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3 2.5V13"/><path d="M12 16h.01"/>' },
  sources: { cls: "sources", svg: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>' },
};

export default {
  name: "AiwV2StructureTab",
  props: {
    rows: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
  },
  data() {
    return { VISUAL_PLACEHOLDER_TYPES, visMenu: false, _sortable: null, listKey: 0, dropHint: null };
  },
  mounted() {
    this._onDragMove = this.onDragMove.bind(this);
    this._dragX = 0;
    this.initSortable();
  },
  beforeDestroy() {
    if (this._sortable) this._sortable.destroy();
    this.teardownDragTracking();
  },
  updated() {
    if (!this._sortable) this.initSortable();
  },
  methods: {
    // FAQ is delivered by the planner as a visual row (visual_type 'faq'), but the
    // design treats it as a section (blue "FAQ" badge + title + word/Qs), not a
    // dashed visual chip. Normalise both shapes to a 'faq' display kind.
    isFaqRow(row) {
      return row.kind === "faq" || (row.kind === "visual" && row.visual_type === "faq");
    },
    rowKind(row) {
      return this.isFaqRow(row) ? "faq" : row.kind;
    },
    rowText(row) {
      if (this.isFaqRow(row)) return row.text || "Frequently asked questions";
      if (row.kind === "intro") return row.text || "Introduction";
      return row.text || "";
    },
    rowWordLabel(row) {
      if (!row.target_words) return "";
      let label = row.target_words + " w";
      const qs = (row.metadata && (row.metadata.question_count || row.metadata.questions)) || 0;
      if (this.isFaqRow(row) && qs) label += " · " + qs + " Qs";
      return label;
    },
    visualLabel(t) {
      const v = VISUAL_PLACEHOLDER_TYPES.find((x) => x.value === t);
      return v ? v.label : t;
    },
    visMeta(t) {
      return VIS[t] || VIS.image;
    },
    visClass(t) {
      // Prefix `vis-` so the modifier never collides with global classes such as
      // Bulma's `.table` / `.image`, which would restyle the chip.
      return "vis-" + this.visMeta(t).cls;
    },
    visIcon(t) {
      return '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" '
        + 'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + this.visMeta(t).svg + '</svg>';
    },
    tagLabel(kind) {
      if (kind === "intro") return "Intro";
      if (kind === "faq") return "FAQ";
      return (kind || "").toUpperCase();
    },
    addVisual(type) {
      this.visMenu = false;
      this.$emit("add-visual", type);
    },
    initSortable() {
      if (!this.$refs.rowList) return;
      this._sortable = new Sortable(this.$refs.rowList, {
        handle: ".st-drag",
        draggable: ".st-row",
        animation: 150,
        // Drive the drag ourselves (forceFallback) so we can read the pointer's X
        // during the drag and offer a depth choice: nest as a child vs top-level.
        forceFallback: true,
        fallbackClass: "st-drag-fly",
        fallbackOnBody: true,
        onStart: (evt) => {
          const dragged = this.rows.find((r) => r.id === evt.item.getAttribute("data-id"));
          const kind = dragged ? this.rowKind(dragged) : null;
          // Only leaf rows (H3 / visual) can be re-nested; an H2 group always moves
          // as a section, so it never offers the "child" depth.
          this._dragLeaf = kind === "h3" || kind === "visual";
          // Snap starts at the row's CURRENT level; nudging left/right changes it.
          this._prevAsChild = kind === "h3" || (kind === "visual" && !!dragged.parent_id);
          this._startX = null; // established on the first move
          document.addEventListener("mousemove", this._onDragMove, true);
          document.addEventListener("touchmove", this._onDragMove, true);
        },
        onEnd: (evt) => {
          this.teardownDragTracking();
          const asChild = this._dragLeaf && this.dropHint ? this.dropHint.asChild : null;
          this.dropHint = null;
          if (evt.oldIndex == null || evt.newIndex == null) return;
          // A same-slot drop still matters when the depth (child vs top-level) changed.
          if (evt.oldIndex === evt.newIndex && asChild == null) return;
          const newRows = this.reorderWithChildren(evt.oldIndex, evt.newIndex, {
            movedId: evt.item.getAttribute("data-id"),
            asChild,
          });
          if (!newRows) return;
          // Sortable only relocates the single dragged element, leaving an H2's
          // H3 + visual children behind in the DOM. Rather than hand-patch the
          // DOM, throw the whole list away (destroy Sortable + bump the container
          // key) and let Vue re-render every row from newRows in the new order;
          // updated() re-inits Sortable on the fresh container.
          if (this._sortable) { this._sortable.destroy(); this._sortable = null; }
          this.listKey += 1;
          this.$emit("reorder", newRows);
        },
      });
    },
    // Follow the pointer during a drag to decide the drop depth + drive the cue.
    onDragMove(e) {
      const pt = e.touches && e.touches[0] ? e.touches[0] : e;
      if (pt) this._dragX = pt.clientX;
      if (this._startX == null) this._startX = this._dragX;
      this.updateDropHint();
    },
    teardownDragTracking() {
      document.removeEventListener("mousemove", this._onDragMove, true);
      document.removeEventListener("touchmove", this._onDragMove, true);
    },
    updateDropHint() {
      const listEl = this.$refs.rowList;
      const ghost = listEl && listEl.querySelector(".sortable-ghost");
      if (!ghost) { this.dropHint = null; return; }
      // Nearest heading above the current drop slot (live DOM order).
      let heading = null;
      for (let el = ghost.previousElementSibling; el; el = el.previousElementSibling) {
        const row = this.rows.find((r) => r.id === el.getAttribute("data-id"));
        if (row && ["h2", "intro", "faq"].includes(this.rowKind(row))) { heading = row; break; }
      }
      // Nudge right of the grab point to nest, left to un-nest; a dead-band near
      // the start keeps the level steady. The remembered intent survives a missing
      // heading (dragged above all sections) so it restores when back in range.
      const delta = this._dragX - (this._startX == null ? this._dragX : this._startX);
      let level = this._prevAsChild;
      if (delta > 16) level = true;
      else if (delta < -16) level = false;
      this._prevAsChild = level;
      this.dropHint = { asChild: !!this._dragLeaf && !!heading && level };
    },
    isAnchor(row) {
      // Block starters: an H2/intro/FAQ owns the H3 + visual rows that follow it.
      return ["h2", "intro", "faq"].includes(row.kind);
    },
    // Reorder after a drag. Dragging an H2 (or intro/FAQ) moves its whole block
    // — the H3 + visual rows that follow it — as one unit; dragging an H3/visual
    // moves just that row. An H2 dropped among another heading's children snaps
    // to a block boundary (before/after that block, by drag direction) so it
    // always lands somewhere valid instead of splitting a block or reverting.
    reorderWithChildren(oldIndex, newIndex, intent) {
      const orig = this.rows;
      if (oldIndex == null || newIndex == null || !orig[oldIndex]) return null;

      // Partition rows into blocks: each block = an anchor plus the H3/visual
      // rows that follow it (a leading non-anchor starts its own block).
      const blocks = [];
      for (const r of orig) {
        if (this.isAnchor(r) || !blocks.length) blocks.push([r]);
        else blocks[blocks.length - 1].push(r);
      }
      const blockOf = [];
      blocks.forEach((b, bi) => b.forEach(() => blockOf.push(bi)));

      const movedBi = blockOf[oldIndex];
      // Dragging a child (not the block's anchor) moves just that row; it
      // re-attaches under whatever heading it lands beneath.
      if (blocks[movedBi][0] !== orig[oldIndex]) {
        const arr = orig.slice();
        const [m] = arr.splice(oldIndex, 1);
        let insertAt = Math.max(0, Math.min(newIndex, arr.length));
        // A visual (list / table / image) can never be a parent. If it's dropped at
        // the top level mid-section, push it past that section's remaining children
        // to the next heading boundary, so nothing ends up appearing nested under it.
        if (intent && intent.asChild === false && this.rowKind(m) === "visual") {
          while (insertAt < arr.length && !["h2", "intro", "faq"].includes(this.rowKind(arr[insertAt]))) {
            insertAt += 1;
          }
        }
        arr.splice(insertAt, 0, m);
        return this.finalizeRows(arr, intent);
      }

      // Dragging an anchor moves the whole block to a block boundary. Sortable's
      // newIndex is where the single anchor landed; snap before/after the block
      // it was dropped on, by drag direction.
      const dropBi = blockOf[Math.max(0, Math.min(newIndex, orig.length - 1))];
      let targetBi = newIndex > oldIndex ? dropBi + 1 : dropBi;
      const movedBlock = blocks[movedBi];
      const rest = blocks.filter((_, i) => i !== movedBi);
      if (movedBi < targetBi) targetBi -= 1; // account for the removed block
      targetBi = Math.max(0, Math.min(targetBi, rest.length));
      rest.splice(targetBi, 0, movedBlock);
      return this.finalizeRows(rest.flat(), intent);
    },
    // Re-derive parentage from position after a move, then renumber `order`.
    // Default (no intent): a visual takes the nearest preceding heading as its
    // parent — or null (standalone top-level) when dragged out of every section —
    // and an H3 above all H2s is promoted to a top-level H2.
    // With an explicit depth `intent` from the drag, the moved leaf honours the
    // user's choice: `asChild` nests it under the section above (H3 stays H3 /
    // visual gets that parent); otherwise it becomes top-level (visual loses its
    // parent; H3 is promoted to a new H2 that parents the rows below it).
    finalizeRows(rows, intent) {
      const movedId = intent && intent.movedId;
      const hasIntent = !!intent && intent.asChild != null;
      let lastH2 = null;
      let lastAnchor = null;
      return rows.map((r, i) => {
        const out = { ...r, order: (i + 1) * 10 };
        const kind = this.rowKind(out);
        const applyIntent = hasIntent && out.id === movedId && (kind === "h3" || kind === "visual");

        if (applyIntent) {
          if (intent.asChild) {
            if (kind === "visual") out.parent_id = lastAnchor || null;
            else if (lastH2) out.parent_id = lastH2;        // H3 under the section above
            else { out.kind = "h2"; out.parent_id = null; } // no H2 above → top-level
          } else if (kind === "visual") {
            out.parent_id = null;                           // standalone top-level visual
          } else {
            out.kind = "h2"; out.parent_id = null;          // H3 → new top-level section
          }
        } else if (kind === "h2" || kind === "intro" || kind === "faq") {
          lastAnchor = out.id;
          if (kind === "h2") lastH2 = out.id;
          return out;
        } else if (kind === "h3") {
          if (lastH2) out.parent_id = lastH2;
          else { out.kind = "h2"; out.parent_id = null; }
        } else {
          out.parent_id = lastAnchor || null;
        }
        // A row that ended up as a heading (incl. a just-promoted moved row)
        // becomes the parent context for the rows below it.
        const fk = this.rowKind(out);
        if (fk === "h2" || fk === "intro" || fk === "faq") {
          lastAnchor = out.id;
          if (fk === "h2") lastH2 = out.id;
        }
        return out;
      });
    },
  },
};
</script>

<style scoped>
/* Pinned TOC row (design, verbatim) */
.st-row.toc-row { background: var(--pop-blue-tint, #e9f0ff); border-color: var(--pop-blue-soft, #c7d5f9); border-style: dashed; }
.st-toc-chip { flex: 1; display: flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 700; color: var(--pop-blue, #316bff); }
.st-toc-chip .vico { width: 16px; height: 16px; }
.st-toc-chip .vico svg { width: 16px; height: 16px; color: var(--pop-blue, #316bff); }

.st-tab { margin: 0 auto; }

/* ----- Rows ----- */
.st-list { display: flex; flex-direction: column; gap: 0; }
/* Tree-style nesting: the placeholder itself snaps between top-level and child
   indent as you nudge the drag left/right — that snap IS the drop cue. */
.st-row.sortable-ghost {
  outline: 2px dashed var(--pop-orange, #ff5700); outline-offset: 1px;
  background: var(--pop-orange-soft, #ffeddd);
  transition: margin-left 0.12s ease;
}
/* The snap owns the placeholder indent — override the row's own lvl3 / vis-child
   margin so nudging left really returns a child row to top level (and vice-versa). */
.st-list .st-row.sortable-ghost { margin-left: 0; }
.st-list.drop-child .st-row.sortable-ghost { margin-left: 30px; }
/* Hide the pointer-following clone — the snapping placeholder is the item. */
.st-row.st-drag-fly { opacity: 0; }
.st-row {
  display: flex; align-items: center; gap: 10px;
  background: #fff; border: 1px solid var(--border, #e4e4e4);
  border-radius: var(--r-lg, 10px); padding: 12px 14px; margin-bottom: 8px;
  position: relative;
}
.st-row.lvl3 { margin-left: 30px; }
.st-row.is-locked { background: #fffaf6; border-color: var(--pop-orange-tint, #ffd9c2); }
.st-row.vis { background: var(--surface-2, #f7f7f7); border-style: dashed; padding: 9px 14px; }
/* A visual with a parent heading is a child of it — indent it like an H3. */
.st-row.vis-child { margin-left: 30px; }
.st-drag { flex-shrink: 0; width: 18px; cursor: grab; color: var(--text-3, #6b7b7e); display: flex; align-items: center; justify-content: center; line-height: 0; }
.st-drag:active { cursor: grabbing; }
.st-drag svg { width: 16px; height: 16px; }

.st-tag {
  flex-shrink: 0; min-width: 38px; text-align: center;
  font-size: 11px; font-weight: 800; letter-spacing: .02em;
  padding: 5px 9px; border-radius: 6px; cursor: pointer; user-select: none;
  background: var(--surface-2, #f7f7f7); color: var(--text-2, #014751); border: 1px solid var(--border, #e4e4e4);
  transition: background .12s;
}
.st-tag:hover { background: #eceef0; }
.st-tag.h3 { background: #fff; color: var(--text-3, #6b7b7e); }
.st-tag.intro { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange-deep, #b23d00); border-color: var(--pop-orange-tint, #ffd9c2); cursor: default; }
.st-tag.faq { background: var(--pop-blue-soft, #c7ddff); color: var(--pop-blue, #316bff); border-color: var(--pop-blue-tint, #e9f3ff); cursor: default; }

.st-text {
  flex: 1; font-size: 14px; color: var(--text, #052e33); line-height: 1.4;
  padding: 4px 6px; border-radius: 5px; cursor: text; outline: none;
  border: 1px solid transparent; min-width: 0;
}
.st-row.lvl3 .st-text { font-size: 13px; color: var(--text-2, #014751); }
.st-text:hover { background: var(--surface-2, #f7f7f7); }
.st-text[contenteditable="true"]:focus { background: #fff; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.st-word { flex-shrink: 0; font-size: 12px; color: var(--text-3, #6b7b7e); font-weight: 600; white-space: nowrap; }

.st-actions { flex-shrink: 0; display: flex; gap: 2px; }
.st-act { width: 28px; height: 28px; border: none; background: transparent; border-radius: 6px; color: var(--text-3, #6b7b7e); cursor: pointer; display: flex; align-items: center; justify-content: center; }
.st-act:hover { background: var(--surface-2, #f7f7f7); color: var(--text-2, #014751); }
.st-act svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.st-act.locked { color: var(--pop-orange-deep, #b23d00); background: var(--pop-orange-soft, #ffeddd); }

.st-vis-chip { flex: 1; display: flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 700; color: var(--text-2, #014751); line-height: 1.2; }
.st-vis-chip .vico { width: 16px; height: 16px; flex-shrink: 0; color: var(--text-3, #6b7b7e); display: inline-flex; align-items: center; justify-content: center; }
.st-vis-chip .vico :deep(svg) { display: block; }
.st-vis-chip.vis-image .vico { color: #2e7d9a; }
.st-vis-chip.vis-table .vico { color: #7a5aa8; }
.st-vis-chip.vis-list .vico { color: #1f8a5b; }
.st-vis-chip.vis-form .vico { color: #2e7d9a; }
.st-vis-chip.vis-sources .vico { color: #7a5aa8; }
.st-vis-chip.vis-highlights .vico { color: var(--pop-orange-deep, #b23d00); }
.st-vis-chip.vis-faqv .vico { color: var(--pop-blue, #316bff); }

.st-empty { color: var(--text-3, #6b7b7e); text-align: center; padding: 20px; }

/* ----- Toolbar ----- */
.st-toolbar { display: flex; align-items: center; gap: 6px; margin: 14px 0 0; flex-wrap: wrap; }
.st-tool {
  height: 38px; min-width: 38px; padding: 0 9px;
  border: 1px solid var(--border, #e4e4e4); background: #fff; border-radius: 8px;
  color: var(--text-2, #014751); cursor: pointer; position: relative;
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  font-family: inherit; font-size: 13px; font-weight: 700;
}
.st-tool svg { width: 17px; height: 17px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
/* Solid (Material) icons shared with the Title step's outline-actions: fill, not stroke. */
.st-tool svg.ic-solid { fill: currentColor; stroke: none; }
.st-tool:hover { border-color: var(--pop-orange, #ff5700); color: var(--pop-orange-deep, #b23d00); background: var(--pop-orange-soft, #ffeddd); }
.st-tool:disabled { opacity: .5; cursor: not-allowed; }
.st-tool.wide { padding: 0 14px; }
.st-tool-sep { width: 1px; height: 24px; background: var(--border, #e4e4e4); margin: 0 4px; }
.st-tool[data-tip]:hover::after {
  content: attr(data-tip); position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  background: #1b1b1b; color: #fff; font-size: 11px; font-weight: 600; white-space: nowrap;
  padding: 5px 9px; border-radius: 6px; z-index: 20; pointer-events: none;
}
.st-tool-wrap { position: relative; display: inline-flex; }
.st-vis-menu {
  position: absolute; top: 44px; left: 0; z-index: 30; background: #fff;
  border: 1px solid var(--border, #e4e4e4); border-radius: 10px;
  box-shadow: var(--shadow-lg, 0 10px 36px rgba(15,20,25,.08)); padding: 6px; min-width: 158px;
}
.st-vis-menu button {
  display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
  border: none; background: transparent; font-family: inherit; font-size: 13px; font-weight: 600;
  color: var(--text-2, #014751); padding: 9px 10px; border-radius: 7px; cursor: pointer;
}
.st-vis-menu button:hover { background: var(--surface-2, #f7f7f7); }
.st-vis-menu svg { width: 17px; height: 17px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }
</style>
